import { CheckCircle2, Circle, Copy, Gift } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../context/LangContext";
import { AnimatedEmblem } from "../shop/AnimatedEmblem";
import { LevelBar } from "./Bits";
import { EARN_WAYS, useCopy } from "./JoinParts";

const Banner = () => {
  const { tr } = useLang();
  return (
    <div className="relative rounded-[24px] bg-[#120E1F] grain overflow-hidden p-6 sm:p-8">
      <div className="psychic-glow absolute -right-10 -top-16 h-72 w-72 rounded-full" />
      <div className="absolute right-6 top-1/2 -translate-y-1/2"><AnimatedEmblem className="h-24 w-24 sm:h-32 sm:w-32" testid="points-emblem" /></div>
      <h2 className="relative font-display text-4xl text-white">Jolgen {tr("Punkte", "Points")}</h2>
      <p className="relative text-sm text-white/60 mt-2 max-w-sm">{tr("Sammle Punkte, schalte Belohnungen frei und erhalte exklusive Member-Vorteile.", "Collect points, unlock rewards and get exclusive member benefits.")}</p>
    </div>
  );
};

export const PointsPanel = ({ me, reload }) => {
  const { tr, lang } = useLang();
  const [copied, copy] = useCopy();
  const link = `${window.location.origin}/join?ref=${me.referral_code}`;
  const redeem = async (r) => {
    try { const { data } = await api.post("/rewards/redeem", { reward_id: r.id }); toast.success(`${tr("Gutschein", "Voucher")} ${data.code} · ${data.amount} €`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="points-panel">
      <Banner />
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-[#120E1F]/5 p-5"><p className="text-xs text-[#707783]">{tr("Deine Punkte", "Your points")}</p>
          <p className="font-mono text-4xl text-[#120E1F] mt-1" data-testid="points-balance">{me.points.toLocaleString("de-DE")}</p></div>
        <div className="bg-white rounded-2xl border border-[#120E1F]/5 p-5 space-y-2"><p className="text-xs text-[#707783]">{tr("Dein Level", "Your level")}</p>
          <p className="font-display text-3xl" data-testid="points-level">Level {me.level} <span className="text-sm text-[#6E3FB8] font-sans">{me.level_name}</span></p><LevelBar m={me} /></div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-[#120E1F]/5 p-5">
          <h3 className="font-display text-2xl mb-3">{tr("So sammelst du Punkte", "Ways to earn points")}</h3>
          <ul className="divide-y">
            {EARN_WAYS.map(([k, I, de, en, pts]) => (
              <li key={k} className="flex items-center gap-3 py-3 text-sm" data-testid={`earn-${k}`}>
                <I className="h-4 w-4 text-[#6E3FB8]" /><span className="flex-1">{lang === "en" ? en : de}</span>
                <span className="font-mono text-xs text-[#B8860B] font-semibold">{pts}</span>
                {me.earned_kinds.includes(k) ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-[#120E1F]/20" />}
              </li>
            ))}
          </ul>
          <div className="mt-4 rounded-xl bg-[#F7F5FB] p-4 space-y-2">
            <p className="text-xs font-semibold">{tr("Dein Einladungslink (+300 für dich, +100 für deine:n Freund:in)", "Your invite link (+300 for you, +100 for your friend)")}</p>
            <div className="flex gap-2"><input readOnly value={link} className="flex-1 rounded-lg border px-3 py-2 text-xs font-mono bg-white" data-testid="referral-link" />
              <button onClick={() => copy(link)} className="btn-outline" data-testid="copy-referral-btn"><Copy className="h-4 w-4" />{copied ? "✓" : tr("Kopieren", "Copy")}</button></div>
          </div>
        </div>
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-[#120E1F]/5 p-5 space-y-3">
            <h3 className="font-display text-2xl">{tr("Prämien-Shop", "Rewards shop")}</h3>
            {me.rewards.map((r) => (
              <div key={r.id} className="flex items-center gap-4 rounded-xl border border-[#120E1F]/5 p-4" data-testid={`reward-${r.id}`}>
                <Gift className="h-6 w-6 text-[#F2C230]" />
                <div className="flex-1"><p className="font-semibold">{r.amount} € {tr("Gutschein", "voucher")}</p><p className="text-xs font-mono text-[#707783]">{r.points} {tr("Punkte", "points")}</p></div>
                <button disabled={me.points < r.points} onClick={() => redeem(r)} className="btn-gold disabled:opacity-40" data-testid={`redeem-${r.id}`}>{tr("Einlösen", "Redeem")}</button>
              </div>
            ))}
            <p className="text-[11px] text-[#707783]">{tr("Gutscheine sind ab dem Shop-Launch im Checkout einlösbar.", "Vouchers can be used at checkout once the shop launches.")}</p>
          </div>
          {me.coupons.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#120E1F]/5 p-5 space-y-2" data-testid="my-coupons">
              <h3 className="font-display text-xl">{tr("Meine Gutscheine", "My vouchers")}</h3>
              {me.coupons.map((c) => <div key={c.code} className="flex justify-between text-sm"><span className="font-mono">{c.code}</span><span className={c.used ? "text-[#707783] line-through" : "text-emerald-700 font-semibold"}>{c.amount} €</span></div>)}
            </div>
          )}
          <div className="bg-white rounded-2xl border border-[#120E1F]/5 p-5" data-testid="points-history">
            <h3 className="font-display text-xl mb-2">{tr("Verlauf", "History")}</h3>
            <ul className="text-sm divide-y max-h-64 overflow-y-auto">
              {me.ledger.map((e) => <li key={`${e.key}-${e.created_at}`} className="py-2 flex justify-between"><span className="capitalize">{e.kind}{e.note ? ` · ${e.note}` : ""}</span><span className={`font-mono ${e.points > 0 ? "text-emerald-700" : "text-red-600"}`}>{e.points > 0 ? "+" : ""}{e.points}</span></li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
